/**
 * Routing tables for cross-entity reference chips. A reference chip's href
 * is a resolver-route URL (e.g. `/site-groups/42/`); EntityRedirect mounts
 * there and needs to know the entity's actual destination page. This module
 * is the one place both directions of that mapping live.
 *
 * Contains:
 * - RESOLVER_ROUTE — type → resolver-route path builder (from paths.js);
 *   only the 5 resolvable reference types are present, so an unlisted type
 *   has no route. Exported so EntityRedirect can derive each route's param
 *   name from the same builder pattern resolveRefRoute uses.
 * - REQUIRES_FETCH — reference types whose destination page depends on the
 *   fetched entity (site-group, site, user); customer and radio resolve
 *   from the id alone
 * - entityRedirectTarget — (type, id, entity) → destination URL or null
 *
 * Only imports paths.js — no RTK Query / redux weight for callers that just
 * need the routing tables.
 *
 * Returns plain JS — no JSX.
 */
import {
  customerPlantsPath,
  customerResolverPath,
  customerUserPath,
  radioResolverPath,
  siteGroupResolverPath,
  siteGroupStructuresPath,
  siteResolverPath,
  sitePath,
  supportRadioDetailPath,
  supportUserPath,
  userResolverPath,
  siteGroupLandingPath,
} from '@sophocles/router/paths';

/** type → resolver-route path builder; only the 5 resolvable reference
 * types are present, so an unlisted type has no route. */
export const RESOLVER_ROUTE = {
  customer: customerResolverPath,
  'site-group': siteGroupResolverPath,
  site: siteResolverPath,
  user: userResolverPath,
  radio: radioResolverPath,
};

/** type → the single param name in its resolver builder's pattern. */
const RESOLVER_PARAM_NAME = Object.fromEntries(
  Object.entries(RESOLVER_ROUTE).map(([type, builder]) => [type, builder.pattern.match(/:(\w+)/)[1]]),
);

/**
 * Returns the resolver-route href for a reference chip, or null when the
 * type has no registered destination or the id isn't known yet (a null id
 * would otherwise throw inside the path builder).
 *
 * @param {string} type
 * @param {string|number} id
 * @returns {string | null}
 */
export const resolveRefRoute = (type, id) => {
  if (id === undefined || id === null) return null;
  const builder = RESOLVER_ROUTE[type];
  return builder ? `${builder({ [RESOLVER_PARAM_NAME[type]]: id })}/` : null;
};

/** Reference types whose destination page requires the fetched entity. */
export const REQUIRES_FETCH = new Set(['site-group', 'site', 'user']);

/**
 * Resolves a reference to its destination page.
 *
 * customer and radio derive their destination from the id alone. site-group,
 * site, and user need the fetched entity to locate a parent scope and return
 * null (no navigable destination yet) until it resolves.
 *
 * @param {string} type - the reference's entity type.
 * @param {string|number} id - the reference's entity id.
 * @param {object|undefined} entity - the fetched entity for REQUIRES_FETCH
 *   types; ignored for customer/radio.
 * @returns {string | null}
 */
export default function entityRedirectTarget(type, id, entity) {
  switch (type) {
    case 'customer':
      return customerPlantsPath({ cust_id: id });
    case 'radio':
      return supportRadioDetailPath({ radio_id: id });
    case 'site-group':
      return entity?.customer?.id
        ? siteGroupLandingPath({ id, is_inventory: entity.is_inventory }, entity.customer.id)
        : null;
    case 'site': {
      const owner = entity?.site?.owner;
      return owner?.id && owner?.customer?.id
        ? sitePath({ cust_id: owner.customer.id, sitegroup_id: owner.id, site_id: id })
        : null;
    }
    case 'user':
      return entity?.customer?.id
        ? customerUserPath({ cust_id: entity.customer.id, user_id: id })
        : supportUserPath({ user_id: id });
    default:
      return null;
  }
}
